/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { HTMLAttributes, useEffect, useState, useRef } from 'preact/compat';
import { Container, Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import { ProductSearchResult, SearchFilter, SearchFacet, SearchVariables, FacetBucket, Scope } from '@/plp/data/models/api';
import { getBucketLabel, getCurrencyType, getFilterSelection } from './utils/facetUtils';
import FacetList from '@/plp/components/FacetList';
import { Button, Checkbox, Icon, RadioButton } from '@adobe-commerce/elsie/components';
import { Facet } from '@/plp/components';
import { VNode } from 'preact';
import { Close } from '@adobe-commerce/elsie/icons';
import { events } from '@adobe-commerce/event-bus';
import { useText } from '@adobe-commerce/elsie/i18n';
import { search } from '@/plp/api';

export interface FacetsProps extends HTMLAttributes<HTMLDivElement> {
  scope?: Scope;
  slots?: {
    Facet?: SlotProps<{ data: SearchFacet }>;
    SelectedFacets?: SlotProps<{ data: SearchFacet[] }>;
    Facets?: SlotProps<{ data: SearchFacet[] }>;
    FacetBucket?: SlotProps<{ data: FacetBucket }>;
    FacetBucketLabel?: SlotProps<{ data: FacetBucket }>;
  };
}

export const Facets: Container<FacetsProps> = ({ scope, slots }) => {
  const labels = useText({
    clearAll: 'Search.Facet.clearAll',
  });

  const [facetList, setFacetList] = useState<SearchFacet[]>([]);
  const [currency, setCurrency] = useState<string | null>(null);
  const [currentSearchVariables, setCurrentSearchVariables] = useState<SearchVariables>();
  const [pendingFilters, setPendingFilters] = useState<SearchFilter[] | null>(null);
  const searchTimeoutRef = useRef<number | null>(null);

  const handleSearchRequest = (searchVariables: SearchVariables): void => {
    // Attributes that appear in the facet list are driven by user selection; all others
    // (visibility, etc.) are carried through so they are not lost when the user changes facets.
    const facetAttributes = new Set(facetList.map((f) => f.attribute));
    const baseFilters = (currentSearchVariables?.filter || []).filter(
      (f) => !facetAttributes.has(f.attribute)
    );
    const mergedFilter = [...baseFilters, ...(searchVariables.filter || [])];

    search({
      ...searchVariables,
      filter: mergedFilter,
    }, { scope });
  };

  const handleSearchResult = (payload: { result: ProductSearchResult; request: SearchVariables }): void => {
    if (!payload || (!payload.result.facets && !payload.result.items && !payload.request.filter)) {
      setFacetList([]);
      return;
    }

    setCurrentSearchVariables(payload.request);

    const { facets, items } = payload.result;

    setCurrency(getCurrencyType(items));

    const filterList = payload.request.filter;

    if (facets) {
      const facetsWithSelections = facets.map((facet: SearchFacet) => {
        const selectedBuckets = facet.buckets.map((bucket) => {
          const selectedFilter = filterList?.find((filter) => filter.attribute === facet.attribute);
          if (selectedFilter) {
            const selectedRange = selectedFilter.range;
            if (selectedRange && bucket.from === selectedRange.from && bucket.to === selectedRange.to) {
              return { ...bucket, selected: true };
            } else if (selectedFilter.in && selectedFilter.in.includes(bucket.title)) {
              return { ...bucket, selected: true };
            }
          }
          return { ...bucket, selected: false };
        });
        return { ...facet, buckets: selectedBuckets };
      });

      setFacetList(facetsWithSelections);
    }
  };

  const updateFacetSelections = (facets: SearchFacet[]): void => {
    // Use functional update to ensure we're working with latest state
    setFacetList(currentFacetList => {
      const updatedFacetList = currentFacetList.map((f) => {
        const newFacet = facets.find((facet) => facet.attribute === f.attribute);
        if (newFacet) {
          return newFacet;
        }
        return f;
      });
      
      // Calculate selectedFilterList from the updated facet list
      const selectedFilterList = updatedFacetList.reduce((accu: SearchFilter[], facet: SearchFacet) => {
        const selectedFilter = getFilterSelection(facet);
        if (selectedFilter) {
          accu.push(selectedFilter as SearchFilter);
        }
        return accu;
      }, [] as SearchFilter[]);
      
      // Queue the search request (will be debounced by useEffect)
      setPendingFilters(selectedFilterList);
      
      return updatedFacetList;
    });
  };

  const handleCancelSelection = (facet: SearchFacet, title: string): void => {
    const newBuckets = facet.buckets.map((bucket) => {
      if (bucket.title === title) {
        return { ...bucket, selected: false };
      }
      return bucket;
    });
    const newFacet = { ...facet, buckets: newBuckets };
    updateFacetSelections([newFacet]);
  };

  const clearAllSelections = (): void => {
    const updatedFacetList = facetList.map((facet) => {
      const updatedBuckets = facet.buckets.map((bucket) => {
        return { ...bucket, selected: false };
      });
      return { ...facet, buckets: updatedBuckets };
    });
    setFacetList(updatedFacetList);
    // Queue the search request with empty filters
    setPendingFilters([]);
  };

  const changeFilter = (event: any, facetAttribute: string): void => {
    const clickedValue = event.target.value;
    const isChecked = event.target.checked;
    
    // Use functional update to work with latest state
    setFacetList(currentFacetList => {
      const currentFacet = currentFacetList.find(f => f.attribute === facetAttribute);
      if (!currentFacet) {
        return currentFacetList;
      }
      
      const newBuckets = currentFacet.buckets.map((bucket) => {
        if (currentFacet.buckets[0]?.__typename === 'RangeBucket') {
          // For range buckets (radio buttons), only one can be selected
          if (bucket.title === clickedValue) {
            return { ...bucket, selected: isChecked };
          }
          return { ...bucket, selected: false };
        }
        // For scalar buckets (checkboxes), multiple can be selected
        if (bucket.title === clickedValue) {
          return { ...bucket, selected: isChecked };
        }
        // Keep other buckets as-is, but create new objects to maintain immutability
        return { ...bucket };
      });
      
      const newFacet = { ...currentFacet, buckets: newBuckets };
      
      // Update the facet list with the new facet
      const updatedFacetList = currentFacetList.map((f) => {
        if (f.attribute === facetAttribute) {
          return newFacet;
        }
        return f;
      });
      
      // Calculate selected filters - will trigger search via useEffect
      const selectedFilterList = updatedFacetList.reduce((accu: SearchFilter[], facet: SearchFacet) => {
        const selectedFilter = getFilterSelection(facet);
        if (selectedFilter) {
          accu.push(selectedFilter as SearchFilter);
        }
        return accu;
      }, [] as SearchFilter[]);
      
      // Queue the search request (will be debounced by useEffect)
      setPendingFilters(selectedFilterList);
      
      return updatedFacetList;
    });
  };

  const getFacetNode = (facet: SearchFacet, index: number): VNode => {
    const header = <span>{facet.title}</span>;
    const buckets = facet.buckets
      .map((bucket: FacetBucket) => {
        const bucketLabel = getBucketLabel(bucket, facet, currency);

        const renderedBucketLabel = (count?: number) => (
          <Slot name="FacetBucketLabel"
            slot={slots?.FacetBucketLabel}
            context={{ data: bucket }}>
            {bucketLabel} {count ? `(${count})` : ''}
          </Slot>
        );
        switch (facet.buckets[0]?.__typename) {
          case 'RangeBucket':
            
            return (
              <Slot name="FacetBucket" slot={slots?.FacetBucket} context={{ data: bucket }}>
                <RadioButton
                  data-testid={`${bucket.title}-radio`}
                  label={renderedBucketLabel()}
                  name={facet.attribute}
                  value={bucket.title}
                  checked={bucket.selected}
                  onChange={(event): void => {
                    changeFilter(event, facet.attribute);
                  }}
                />
              </Slot>
            ) as VNode;
          case 'ScalarBucket':
          case 'CategoryView':
            return (
              <Slot name="FacetBucket" slot={slots?.FacetBucket} context={{ data: bucket }}>
                <Checkbox
                  id={`${facet.attribute}-${bucket.title.replace(/[^a-zA-Z0-9_-]/g, '')}`}
                  data-testid={`${bucket.title}-checkbox`}
                  label={renderedBucketLabel(bucket.count)}
                  name={`${facet.attribute}-${bucket.title.replace(/[^a-zA-Z0-9_-]/g, '')}`}
                  value={bucket.title}
                  checked={bucket.selected}
                  onChange={(event): void => {
                    changeFilter(event, facet.attribute);
                  }}
                />
              </Slot>
            ) as VNode;
          default:
            return null;
        }
      })
      .filter((bucket) => bucket !== null);

    return <Facet header={header} buckets={buckets} last={index === facetList.length - 1} />
  };

  const getFacetsNode = (): VNode => {
    return (
      <Slot name="Facets" slot={slots?.Facets} context={{ data: facetList }}>
        {facetList.map((facet, index) => {
          return facet.buckets.length > 0
            ? ((
              <>
                <Slot name="Facet" slot={slots?.Facet} context={{ data: facet }}>
                  {getFacetNode(facet, index)}
                </Slot>
              </>
            ) as VNode)
            : null;
        })}
      </Slot>
    );
  };

  const getSelectedFacetsNode = (): VNode => {
    const selectedFacets = facetList.reduce((accu, curr) => {
      const selected = curr.buckets.filter((bucket: FacetBucket) => bucket.selected);
      if (selected.length > 0) {
        accu.push(curr);
      }
      return accu;
    }, [] as SearchFacet[]);
    const selectedFilterBtns = selectedFacets.map((facet) => {
      return facet.buckets.map((bucket) => {
        if (bucket.selected) {
          const bucketTitle = getBucketLabel(bucket, facet, currency);
          return (
            <Button
              data-testid={`${bucket.title}-selected-btn`}
              aria-label={`Remove ${facet.title} filter: ${bucket.title}`}
              variant="secondary"
              children={
                <>
                  <span>{bucketTitle}</span>
                  <Icon source={Close} size="16" />
                </>
              }
              onClick={(): void => {
                handleCancelSelection(facet, bucket.title);
              }}
            />
          ) as VNode;
        }
        return null;
      });
    });

    const clearAllBtn =
      selectedFilterBtns.length > 0 ? (
        <Button
          variant="secondary"
          onClick={(): void => {
            clearAllSelections();
          }}
        >
          <span>{labels.clearAll}</span>
        </Button>
      ) : null;
    return (
      <Slot name="SelectedFacets" slot={slots?.SelectedFacets} context={{ data: selectedFacets }}>
        {selectedFilterBtns}
        {clearAllBtn}
      </Slot>
    );
  };

  useEffect(() => {
    const dataEvents = events.on('search/result', handleSearchResult, { eager: true, scope });

    return () => {
      dataEvents?.off();
    };
  }, []);

  // Handle search triggering with debouncing for better UX
  useEffect(() => {
    if (pendingFilters === null) {
      return;
    }

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search requests to avoid too many API calls
    searchTimeoutRef.current = setTimeout(() => {
      if (currentSearchVariables) {
        // Reset to first page when filters change
        handleSearchRequest({ ...currentSearchVariables, filter: pendingFilters, currentPage: 1 });
      }
      setPendingFilters(null);
    }, 150);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [pendingFilters, currentSearchVariables]);

  return <FacetList selectedFacets={getSelectedFacetsNode()} facets={getFacetsNode()} />;
};
